import { SWR_KEYS } from '@bulknutrients/bulk-shared-lib/constants';
import { isErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import { getErrorMessage } from '@bulknutrients/bulk-shared-lib/utils';
import { log } from '@lib/logs';
import type { RemoveCartItemAPIResponse } from 'pages/api/cart';
import { useSWRConfig } from 'swr';

interface ReturnType {
  data?: null;
  error?: string;
}

const removeQueue: (() => Promise<void>)[] = [];
let processing = false;

async function processQueue(mutate) {
  processing = true;

  try {
    while (removeQueue.length > 0) {
      const nextRequest = removeQueue[0];
      await nextRequest();
      removeQueue.shift();
    }
  } catch (error) {
    // remove the failed request from the queue
    removeQueue.shift();
  } finally {
    // All requests processed, update the cart data
    mutate(SWR_KEYS.cart);
    processing = false;
  }
}

export function useRemoveItem({ include }: { include?: string[] }) {
  const { mutate } = useSWRConfig();

  return async ({ id }: { id: string }): Promise<ReturnType> => {
    const apiUrl = new URL('/api/cart', 'https://a');
    apiUrl.searchParams.set('itemId', id);

    if (include) {
      apiUrl.searchParams.set('include', include.join(','));
    }

    const request = async () => {
      const response = await fetch(apiUrl.pathname + apiUrl.search, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      try {
        const res: RemoveCartItemAPIResponse = await response.json();
        if (isErrorResponse(res)) {
          return {
            error: res.error.message.join('. '),
          };
        }

        return {
          data: res.data,
        };
      } catch (err) {
        log({ error: err, location: 'useRemoveItem' });
        return {
          error:
            'Unexpected remote server error, may have been unable to remove the item',
        };
      }
    };

    // Add the request to the queue
    const promise: Promise<ReturnType> = new Promise((resolve, reject) => {
      removeQueue.push(async () => {
        try {
          const result = await request();
          resolve(result);
        } catch (error) {
          const message = getErrorMessage(error);
          reject({
            data: null,
            error: message,
          });
        }
      });
    });

    // If there's no processing happening, start processing the queue
    if (!processing) {
      processing = true;
      await processQueue(mutate);
    }

    return promise;
  };
}
